import { useState, useEffect, FormEvent } from "react";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";

const benefits = [
  { icon: "ri-money-dollar-circle-line", title: "Monetize Your Talent", desc: "Earn real income through BIGO Live's reward system with our guidance." },
  { icon: "ri-graduation-cap-line", title: "Expert Training", desc: "Get hands-on coaching from experienced BIGO Live professionals." },
  { icon: "ri-team-line", title: "Join the Community", desc: "Be part of a tight-knit, supportive agency family." },
  { icon: "ri-bar-chart-2-line", title: "Performance Support", desc: "Regular check-ins and strategy sessions to keep you growing." },
];

const SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: "Join as a BIGO Host | The LittyCity Agency LLC",
  url: `${import.meta.env.VITE_SITE_URL || "https://example.com"}/join-bigo`,
  description:
    "Apply to join The LittyCity Agency LLC as a BIGO Live host. Get expert training, monetization coaching, and full creator support from an official BIGO Live agency partner.",
  mainEntity: {
    "@type": "JobPosting",
    title: "BIGO Live Host",
    hiringOrganization: {
      "@type": "Organization",
      name: "The LittyCity Agency LLC",
      url: import.meta.env.VITE_SITE_URL || "https://example.com",
    },
    jobLocation: {
      "@type": "Place",
      address: {
        "@type": "PostalAddress",
        addressCountry: "US",
      },
    },
    description:
      "Join The LittyCity Agency LLC as a BIGO Live host. Earn income through BIGO Live's reward system with expert training, performance support, and a supportive creator community.",
    employmentType: "CONTRACTOR",
  },
};

export default function JoinBigoPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    bigoId: "",
    experience: "",
    content: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("visible");
        });
      },
      { threshold: 0.15 }
    );
    document.querySelectorAll(".reveal").forEach((el) => observer.observe(el));

    const id = "schema-join";
    let el = document.getElementById(id) as HTMLScriptElement | null;
    if (!el) {
      el = document.createElement("script");
      el.id = id;
      el.type = "application/ld+json";
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(SCHEMA);
    document.title = "Join as a BIGO Host | The LittyCity Agency LLC";

    return () => {
      observer.disconnect();
      const s = document.getElementById(id);
      if (s) s.remove();
    };
  }, []);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    const msgLen = formData.message.length;
    if (msgLen > 500) {
      setError("Message must be 500 characters or less.");
      return;
    }
    setSubmitting(true);
    try {
      const body = new URLSearchParams({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        bigoId: formData.bigoId,
        experience: formData.experience,
        content: formData.content,
        message: formData.message,
      });
      const res = await fetch("https://readdy.ai/api/form/d744vf6csbv12mkucvp0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        setError("Submission failed. Please try again.");
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-black">
      <Navbar />

      {/* Header */}
      <div className="pt-36 pb-20 bg-brand-black text-center relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] rounded-full bg-gold/4 blur-3xl" />
        </div>
        <div className="relative z-10">
          <a
            href="https://dhrgjmfi3pg92.cloudfront.net/live/bigo-ssr-fed-render/page/live/pages/bigolive/anchor-recurit/share?code=22hMPR&actId=6696501&t=v1_F01FF39D3082732E68F35ACF9B999061&o=1413743662349206308&lang=en&fbclid=PAT01DUAQ1D2VleHRuA2FlbQIxMABzcnRjBmFwcF9pZA81NjcwNjczNDMzNTI0MjcAAacov9iceJxOB4zVP2i4NXjUhI8deY5wbOajFw0U5wRhAyskx9zMhFOX6C1Ang_aem_awZNRjrqBU1eD95zVXfbXQ"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-gold text-white mb-6 cursor-pointer hover:bg-gold-light transition-colors duration-300"
          >
            <i className="ri-verified-badge-fill text-sm" />
            <span className="font-sans font-bold text-xs tracking-widest uppercase">
              Official BIGO Live Agency
            </span>
          </a>
          <h1 className="font-display font-black text-white text-5xl md:text-7xl leading-none mb-6">
            Join as a{" "}
            <span className="text-gradient-gold">BIGO Host</span>
          </h1>
          <p className="text-white/50 font-sans text-lg max-w-xl mx-auto">
            Apply today and start your journey with The LittyCity Agency LLC.
          </p>
          <div className="w-24 h-px bg-gold mx-auto mt-8" />
        </div>
      </div>

      <main>
        {/* Benefits */}
        <section className="py-20 bg-brand-dark section-divider">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {benefits.map((b, i) => (
                <div
                  key={b.title}
                  className={`reveal reveal-delay-${i + 1} card-gold bg-brand-black rounded-2xl p-7 text-center`}
                >
                  <div className="w-14 h-14 flex items-center justify-center mx-auto rounded-xl border border-gold/30 bg-gold/5 mb-4">
                    <i className={`${b.icon} text-gold text-2xl`} />
                  </div>
                  <h3 className="font-display font-bold text-white text-lg mb-2">{b.title}</h3>
                  <p className="text-white/50 text-sm font-sans leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Application Form */}
        <section className="py-24 md:py-36 bg-brand-black">
          <div className="max-w-3xl mx-auto px-6 md:px-12">
            <div className="text-center mb-14 reveal">
              <span className="text-gold text-xs font-sans font-semibold tracking-[0.3em] uppercase block mb-4">
                Application
              </span>
              <h2 className="font-display font-black text-white text-4xl md:text-5xl">
                Apply <span className="text-gradient-gold">Now</span>
              </h2>
            </div>

            {submitted ? (
              <div className="reveal text-center card-gold bg-brand-card rounded-3xl p-16">
                <div className="w-20 h-20 flex items-center justify-center mx-auto rounded-full bg-gold/10 border border-gold/30 mb-6">
                  <i className="ri-check-line text-gold text-4xl" />
                </div>
                <h3 className="font-display font-bold text-white text-3xl mb-4">
                  Application Received!
                </h3>
                <p className="text-white/60 font-sans text-lg">
                  Thank you for applying to join The LittyCity Agency LLC as a
                  BIGO Host. We&apos;ll review your application and reach out
                  to you shortly.
                </p>
              </div>
            ) : (
              <form
                data-readdy-form
                onSubmit={handleSubmit}
                className="reveal card-gold bg-brand-card rounded-3xl p-8 md:p-12 flex flex-col gap-6"
              >
                {/* Name */}
                <div>
                  <label className="text-gold text-xs font-sans font-semibold tracking-widest uppercase block mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Your full name"
                    className="w-full bg-brand-black border border-gold/20 rounded-xl px-4 py-3 text-white font-sans text-sm placeholder:text-white/30 focus:outline-none focus:border-gold/60 transition-colors duration-300"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {/* Email */}
                  <div>
                    <label className="text-gold text-xs font-sans font-semibold tracking-widest uppercase block mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="your@email.com"
                      className="w-full bg-brand-black border border-gold/20 rounded-xl px-4 py-3 text-white font-sans text-sm placeholder:text-white/30 focus:outline-none focus:border-gold/60 transition-colors duration-300"
                    />
                  </div>
                  {/* Phone */}
                  <div>
                    <label className="text-gold text-xs font-sans font-semibold tracking-widest uppercase block mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+1 (000) 000-0000"
                      className="w-full bg-brand-black border border-gold/20 rounded-xl px-4 py-3 text-white font-sans text-sm placeholder:text-white/30 focus:outline-none focus:border-gold/60 transition-colors duration-300"
                    />
                  </div>
                </div>

                {/* BIGO ID */}
                <div>
                  <label className="text-gold text-xs font-sans font-semibold tracking-widest uppercase block mb-2">
                    BIGO Live ID (if you have one)
                  </label>
                  <input
                    type="text"
                    name="bigoId"
                    value={formData.bigoId}
                    onChange={handleChange}
                    placeholder="Your BIGO Live ID"
                    className="w-full bg-brand-black border border-gold/20 rounded-xl px-4 py-3 text-white font-sans text-sm placeholder:text-white/30 focus:outline-none focus:border-gold/60 transition-colors duration-300"
                  />
                </div>

                {/* Experience Level */}
                <div>
                  <label className="text-gold text-xs font-sans font-semibold tracking-widest uppercase block mb-2">
                    Streaming Experience *
                  </label>
                  <select
                    name="experience"
                    value={formData.experience}
                    onChange={handleChange}
                    required
                    className="w-full bg-brand-black border border-gold/20 rounded-xl px-4 py-3 text-white font-sans text-sm focus:outline-none focus:border-gold/60 transition-colors duration-300"
                  >
                    <option value="" disabled>
                      Select your experience level
                    </option>
                    <option value="Beginner — Never streamed before">Beginner — Never streamed before</option>
                    <option value="Intermediate — Some streaming experience">Intermediate — Some streaming experience</option>
                    <option value="Experienced — Regular streamer">Experienced — Regular streamer</option>
                    <option value="Advanced — Professional or full-time streamer">Advanced — Professional or full-time streamer</option>
                  </select>
                </div>

                {/* Content Type */}
                <div>
                  <label className="text-gold text-xs font-sans font-semibold tracking-widest uppercase block mb-2">
                    Content Type / Niche *
                  </label>
                  <input
                    type="text"
                    name="content"
                    value={formData.content}
                    onChange={handleChange}
                    required
                    placeholder="e.g. Music, Gaming, Lifestyle, Talk Shows..."
                    className="w-full bg-brand-black border border-gold/20 rounded-xl px-4 py-3 text-white font-sans text-sm placeholder:text-white/30 focus:outline-none focus:border-gold/60 transition-colors duration-300"
                  />
                </div>

                {/* Message */}
                <div>
                  <label className="text-gold text-xs font-sans font-semibold tracking-widest uppercase block mb-2">
                    Why Do You Want to Join? (max 500 chars)
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    maxLength={500}
                    placeholder="Tell us a little about yourself and your goals..."
                    className="w-full bg-brand-black border border-gold/20 rounded-xl px-4 py-3 text-white font-sans text-sm placeholder:text-white/30 focus:outline-none focus:border-gold/60 transition-colors duration-300 resize-none"
                  />
                  <p className="text-white/30 text-xs font-sans mt-1 text-right">
                    {formData.message.length}/500
                  </p>
                </div>

                {error && (
                  <p className="text-red-400 text-sm font-sans">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-gold w-full py-4 rounded-xl text-white font-sans font-bold text-sm tracking-widest cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? "Submitting..." : "Submit Application"}
                </button>
              </form>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
